'use client';

import { cn } from '../../../../lib/utils';
import { CancelFill, Search } from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

const searchFieldVariants = cva(
  [
    'flex items-center h-[38px] px-sm py-2xs gap-3xs border rounded-xs overflow-clip transition-all',
    'cursor-text',
  ],
  {
    variants: {
      state: {
        default:
          'bg-comp-search-field-bg-default border-comp-search-field-border-default hover:bg-comp-search-field-bg-hover',
        focused:
          'bg-comp-search-field-bg-default border-comp-search-field-border-focus',
        disabled:
          'bg-comp-search-field-bg-disabled border-comp-search-field-border-default cursor-not-allowed opacity-50',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

const inputVariants = cva(
  [
    'flex-1 min-w-0 outline-none bg-transparent',
    'text-comp-moderate-medium text-comp-search-field-text-default',
    'placeholder:text-comp-search-field-placeholder',
    'caret-comp-search-field-caret',
    // 브라우저 기본 검색 X 버튼 제거
    '[&::-webkit-search-cancel-button]:appearance-none',
    '[&::-webkit-search-decoration]:appearance-none',
  ],
  {
    variants: {
      state: {
        default: '',
        focused: 'placeholder:text-comp-search-field-placeholder-focus',
        disabled: 'cursor-not-allowed pointer-events-none',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

export interface SearchFieldProps extends Omit<
  React.ComponentProps<'input'>,
  'type'
> {
  /** 컴포넌트 상태 */
  state?: 'default' | 'focused' | 'disabled';
  /** 클리어 버튼 표시 여부 (값이 있을 때만 표시) */
  showClearButton?: boolean;
  /** 클리어 버튼 클릭 시 콜백 */
  onClear?: () => void;
  /** 컨테이너 클래스명 */
  containerClassName?: string;
}

/**
 * SearchField 컴포넌트
 *
 * 검색 입력을 위한 필드 컴포넌트입니다.
 * 검색 아이콘과 클리어 버튼을 포함하며, 다양한 상태를 지원합니다.
 */
const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      state = 'default',
      showClearButton = true,
      onClear,
      containerClassName,
      className,
      placeholder = '무엇을 찾을까요?',
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    // 클리어 버튼 표시용 상태 (DOM 값 기반)
    const [hasValue, setHasValue] = useState(() => {
      const initial = value ?? defaultValue ?? '';
      return typeof initial === 'string' ? initial.length > 0 : !!initial;
    });

    // value prop이 변경되면 hasValue 업데이트 (controlled 모드)
    useEffect(() => {
      if (value !== undefined) {
        setHasValue(typeof value === 'string' ? value.length > 0 : !!value);
      }
    }, [value]);

    // 실제 상태 계산 (disabled prop이 있으면 disabled 상태 우선)
    const computedState = disabled ? 'disabled' : isFocused ? 'focused' : state;

    const handleContainerClick = useCallback(() => {
      if (computedState !== 'disabled') {
        inputRef.current?.focus();
      }
    }, [computedState]);

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // hasValue 상태만 업데이트 (클리어 버튼 표시용)
        setHasValue(e.target.value.length > 0);
        onChange?.(e);
      },
      [onChange],
    );

    const handleClear = useCallback(() => {
      if (inputRef.current) {
        // DOM 직접 조작
        inputRef.current.value = '';
        setHasValue(false);

        // onChange 이벤트 트리거 (synthetic event 생성)
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        )?.set;
        nativeInputValueSetter?.call(inputRef.current, '');

        const event = new Event('input', { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }
      onClear?.();
      inputRef.current?.focus();
    }, [onClear]);

    // controlled vs uncontrolled 결정
    const inputProps =
      value !== undefined
        ? { value } // controlled
        : { defaultValue }; // uncontrolled

    return (
      <div
        className={cn(
          searchFieldVariants({ state: computedState }),
          containerClassName,
        )}
        onClick={handleContainerClick}
      >
        {/* 검색 아이콘 */}
        <Search
          className={cn(
            'size-[20px] shrink-0 transition-colors',
            hasValue || isFocused
              ? '[&>g>path]:fill-sys-cont-neutral-default'
              : '[&>g>path]:fill-sys-cont-neutral-lightest',
          )}
        />

        {/* 입력 필드 */}
        <input
          ref={(node) => {
            inputRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          type="search"
          className={cn(inputVariants({ state: computedState }), className)}
          placeholder={placeholder}
          {...inputProps}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled || state === 'disabled'}
          {...rest}
        />

        {/* 클리어 버튼 */}
        {showClearButton && hasValue && computedState !== 'disabled' && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="shrink-0 cursor-pointer border-0 bg-transparent p-0 transition-opacity hover:opacity-70"
            aria-label="입력 내용 지우기"
          >
            <CancelFill className="size-[20px] [&>g>path]:fill-sys-cont-neutral-light" />
          </button>
        )}
      </div>
    );
  },
);

SearchField.displayName = 'SearchField';

export default SearchField;
